/**
 * Fetch wrapper for the HSI Label API (ARCHITECTURE §5.1).
 *
 * * Cookies are always sent (`credentials: "include"`).
 * * Unsafe methods echo the `hsl_csrf` cookie in the `X-HSL-CSRF` header (double submit).
 * * Bodies are JSON unless they are already a `BodyInit` (Blob, FormData, ArrayBuffer…).
 * * Every non-2xx response becomes an {@link ApiError} (`{status, detail, code, extra}`;
 *   FastAPI 422 bodies are flattened into `detail` and kept in `validation`).
 * * A 401 calls the unauthorized handler (default: go to `/login?next=<current path>`)
 *   unless `redirectOn401: false` or the user is already on a public page.
 */
import type { ValidationErrorItem } from './types';

export const CSRF_COOKIE = 'hsl_csrf';
export const CSRF_HEADER = 'X-HSL-CSRF';

/** Routes that must never bounce to the login page on a 401. */
const PUBLIC_PATH_PREFIXES = ['/login', '/invite'];

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Readonly<Record<string, QueryValue | readonly QueryValue[]>>;
export type HttpMethod = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  /** Query string; `null`/`undefined` values are skipped, arrays repeat the key. */
  query?: QueryParams;
  /** JSON-serialised unless it is a `BodyInit` (Blob, FormData, ArrayBuffer, …). */
  body?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
  /** Call the unauthorized handler on a 401 (default `true`). */
  redirectOn401?: boolean;
  /** `fetch` keepalive: the request outlives the page (autosave on unload). */
  keepalive?: boolean;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

// --------------------------------------------------------------------------- errors

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly code: string | null;
  readonly extra: Readonly<Record<string, unknown>> | null;
  /** FastAPI request-validation issues (422), otherwise empty. */
  readonly validation: readonly ValidationErrorItem[];
  /** Seconds from the `Retry-After` header (503 busy / 429), `null` when absent. */
  readonly retryAfterS: number | null;

  constructor(init: {
    status: number;
    detail: string;
    code?: string | null;
    extra?: Record<string, unknown> | null;
    validation?: readonly ValidationErrorItem[];
    retryAfterS?: number | null;
  }) {
    super(init.detail);
    this.name = 'ApiError';
    this.status = init.status;
    this.detail = init.detail;
    this.code = init.code ?? null;
    this.extra = init.extra ?? null;
    this.validation = init.validation ?? [];
    this.retryAfterS = init.retryAfterS ?? null;
  }

  /**
   * Validation messages keyed by dotted field path, relative to `prefix` (default: the
   * request body). E.g. `loc: ["body", "params", "bands", "lo"]` with
   * `prefix = ["body", "params"]` → `{ "bands.lo": "…" }`. Issues outside the prefix are
   * dropped; an issue on the prefix itself is keyed `""`.
   */
  fieldErrors(prefix: readonly (string | number)[] = ['body']): Record<string, string> {
    const out: Record<string, string> = {};
    for (const issue of this.validation) {
      const loc = issue.loc;
      if (!prefix.every((part, i) => loc[i] === part)) continue;
      const key = loc.slice(prefix.length).join('.');
      out[key] = out[key] ? `${out[key]}; ${issue.msg}` : issue.msg;
    }
    return out;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Error codes of a temporarily overloaded server (503 + `Retry-After`): retry, don't alarm. */
export const BUSY_CODES: readonly string[] = ['busy', 'db_busy'];

/** A 503 "server busy" (a slot, the login hashing gate or the DB pool is full). */
export function isBusyError(error: unknown): error is ApiError {
  return isApiError(error) && error.status === 503 && error.code !== null && BUSY_CODES.includes(error.code);
}

/** Wait before retrying a busy request: `Retry-After` (default 1 s), growing per attempt, ≤ 15 s. */
export function busyRetryDelayMs(error: unknown, attempt: number): number {
  const base = isApiError(error) && error.retryAfterS !== null ? error.retryAfterS * 1000 : 1000;
  const grown = base * Math.min(4, 1 + 0.5 * Math.max(0, attempt - 1));
  return Math.min(15_000, Math.max(250, grown)) * (0.9 + Math.random() * 0.2);
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError');
}

function sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Runs `fn`, retrying while the server answers 503 busy (honouring `Retry-After`). Other
 * errors, aborts and the last busy error after `attempts` tries are thrown.
 */
export async function withBusyRetry<T>(
  fn: () => Promise<T>,
  options: {
    attempts?: number;
    signal?: AbortSignal;
    onRetry?: (error: ApiError, attempt: number) => void;
  } = {},
): Promise<T> {
  const attempts = options.attempts ?? 5;
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (!isBusyError(error) || attempt >= attempts || options.signal?.aborted) throw error;
      options.onRetry?.(error, attempt);
      await sleepMs(busyRetryDelayMs(error, attempt), options.signal);
    }
  }
}

/** Human-readable message of any thrown value (for toasts and inline errors). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return String(error);
}

function isValidationItem(value: unknown): value is ValidationErrorItem {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { loc?: unknown }).loc) &&
    typeof (value as { msg?: unknown }).msg === 'string'
  );
}

function describeIssue(issue: ValidationErrorItem): string {
  const path = issue.loc.filter((part) => part !== 'body' && part !== 'query').join('.');
  return path ? `${path}: ${issue.msg}` : issue.msg;
}

/** `Retry-After` in seconds (delta-seconds or an HTTP date), `null` when absent / invalid. */
export function parseRetryAfter(value: string | null, now = Date.now()): number | null {
  if (!value) return null;
  const seconds = Number(value.trim());
  if (Number.isFinite(seconds)) return Math.max(0, seconds);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, (date - now) / 1000) : null;
}

async function errorFromResponse(response: Response): Promise<ApiError> {
  const error = await errorFromBody(response);
  const retryAfterS = parseRetryAfter(response.headers.get('Retry-After'));
  if (retryAfterS === null) return error;
  return new ApiError({
    status: error.status,
    detail: error.detail,
    code: error.code,
    extra: error.extra ? { ...error.extra } : null,
    validation: error.validation,
    retryAfterS,
  });
}

async function errorFromBody(response: Response): Promise<ApiError> {
  const fallback = response.statusText || `HTTP ${response.status}`;
  let body: unknown;
  try {
    const text = await response.text();
    body = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    body = null;
  }
  if (typeof body === 'object' && body !== null && 'detail' in body) {
    const { detail, code, extra } = body as { detail: unknown; code?: unknown; extra?: unknown };
    if (Array.isArray(detail)) {
      const validation = detail.filter(isValidationItem);
      return new ApiError({
        status: response.status,
        detail: validation.map(describeIssue).join('; ') || fallback,
        code: 'validation_error',
        validation,
      });
    }
    return new ApiError({
      status: response.status,
      detail: typeof detail === 'string' ? detail : fallback,
      code: typeof code === 'string' ? code : null,
      extra: typeof extra === 'object' && extra !== null ? (extra as Record<string, unknown>) : null,
    });
  }
  return new ApiError({ status: response.status, detail: fallback });
}

// --------------------------------------------------------------------------- 401 handling

/** Login page URL that returns to `next` afterwards. */
export function loginPath(next?: string): string {
  return next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login';
}

/**
 * Validates a `?next=` target: only same-origin absolute paths (no `//host`, no scheme),
 * never back to a public page. Falls back to `/`.
 */
export function safeNextPath(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return '/';
  if (PUBLIC_PATH_PREFIXES.some((prefix) => next.startsWith(prefix))) return '/';
  return next;
}

type UnauthorizedHandler = (nextPath: string) => void;

const defaultUnauthorizedHandler: UnauthorizedHandler = (nextPath) => {
  window.location.assign(loginPath(nextPath));
};

let unauthorizedHandler: UnauthorizedHandler = defaultUnauthorizedHandler;

/** Replaces what happens on a 401 (the app wires this to the query cache + router). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler ?? defaultUnauthorizedHandler;
}

function handleUnauthorized(): void {
  const { pathname, search, hash } = window.location;
  if (PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return;
  unauthorizedHandler(`${pathname}${search}${hash}`);
}

// --------------------------------------------------------------------------- request core

export function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() === name) return decodeURIComponent(part.slice(eq + 1).trim());
  }
  return null;
}

/** Absolute URL of an API path (`/api/...`) with an encoded query string. */
export function buildUrl(path: string, query?: QueryParams): string {
  const url = new URL(path, window.location.origin);
  if (query) {
    for (const [key, raw] of Object.entries(query)) {
      const values: readonly QueryValue[] = Array.isArray(raw) ? raw : [raw as QueryValue];
      for (const value of values) {
        if (value === null || value === undefined) continue;
        url.searchParams.append(key, String(value));
      }
    }
  }
  return url.toString();
}

/** A `Blob` from any realm (another frame, or the runtime's own Blob class). */
function isBlobLike(body: unknown): body is Blob {
  if (body instanceof Blob) return true;
  if (typeof body !== 'object' || body === null) return false;
  const tag = (body as { [Symbol.toStringTag]?: unknown })[Symbol.toStringTag];
  return (tag === 'Blob' || tag === 'File') && typeof (body as Blob).arrayBuffer === 'function';
}

function isBodyInit(body: unknown): body is BodyInit {
  return (
    typeof body === 'string' ||
    isBlobLike(body) ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof ReadableStream
  );
}

/** Performs the request and throws {@link ApiError} for non-2xx responses. */
export async function rawRequest(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  const headers = new Headers(options.headers);
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    if (isBodyInit(options.body)) {
      body = options.body;
    } else {
      body = JSON.stringify(options.body);
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    }
  }
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: 'include',
      signal: options.signal,
      keepalive: options.keepalive,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({
      status: 0,
      detail: 'Cannot reach the server. Check your connection and try again.',
      code: 'network_error',
    });
  }
  if (!response.ok) {
    if (response.status === 401 && options.redirectOn401 !== false) handleUnauthorized();
    throw await errorFromResponse(response);
  }
  return response;
}

/** JSON request returning data, status and headers (use when the status matters, e.g. 202). */
export async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  const response = await rawRequest(method, path, options);
  const text = response.status === 204 ? '' : await response.text();
  const data = (text ? JSON.parse(text) : undefined) as T;
  return { data, status: response.status, headers: response.headers };
}

type BodylessOptions = Omit<RequestOptions, 'body'>;

/** Shorthands returning only the parsed body (`undefined` for 204). */
export const api = {
  get: async <T>(path: string, options?: BodylessOptions): Promise<T> =>
    (await request<T>('GET', path, options)).data,
  post: async <T>(path: string, body?: unknown, options?: BodylessOptions): Promise<T> =>
    (await request<T>('POST', path, { ...options, body })).data,
  put: async <T>(path: string, body?: unknown, options?: BodylessOptions): Promise<T> =>
    (await request<T>('PUT', path, { ...options, body })).data,
  patch: async <T>(path: string, body?: unknown, options?: BodylessOptions): Promise<T> =>
    (await request<T>('PATCH', path, { ...options, body })).data,
  delete: async <T = void>(path: string, options?: RequestOptions): Promise<T> =>
    (await request<T>('DELETE', path, options)).data,
};

// --------------------------------------------------------------------------- binary / PNG

export interface BinaryResponse {
  blob: Blob;
  status: number;
  headers: Headers;
}

/** Binary request (PNG planes, similarity maps, downloads). */
export async function requestBinary(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<BinaryResponse> {
  const headers = new Headers(options.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'image/png, application/octet-stream, */*');
  const response = await rawRequest(method, path, { ...options, headers });
  return { blob: await response.blob(), status: response.status, headers: response.headers };
}

/** Parses `X-HSL-Range: lo,hi` (plane quantisation range, §5.2). */
export function parseRangeHeader(value: string | null): [number, number] | null {
  if (!value) return null;
  const parts = value.split(',').map((part) => Number(part.trim()));
  if (parts.length !== 2 || parts.some((n) => !Number.isFinite(n))) return null;
  return [parts[0], parts[1]];
}

export interface PngImage {
  bitmap: ImageBitmap;
  /** `X-HSL-Range` of planes, `null` when absent. */
  range: [number, number] | null;
  headers: Headers;
}

/**
 * Fetches a PNG and decodes it off the main thread. Use for planes and overlays; decode
 * to pixels by drawing the bitmap on an (Offscreen)Canvas. Close the bitmap when done.
 */
export async function fetchPng(
  path: string,
  options: Omit<RequestOptions, 'body'> & { method?: HttpMethod; body?: unknown } = {},
): Promise<PngImage> {
  const { method = 'GET', ...rest } = options;
  const { blob, headers } = await requestBinary(method, path, rest);
  const bitmap = await createImageBitmap(blob, {
    colorSpaceConversion: 'none',
    premultiplyAlpha: 'none',
  });
  return { bitmap, range: parseRangeHeader(headers.get('X-HSL-Range')), headers };
}
